import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Collapse } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import _cloneDeep from 'lodash/cloneDeep'
import style from '../../../../Styles/index.module.scss'
import { useAppDispatch } from '../../../../../../../hooks/useAppDispatch'
import { PATH_JOINER } from '../../../../../utils/getConfigBitPath'
import { generateAriaId } from '../../../../../../ActionsBar/utils/generateAriaId'
import { useExpand } from '../../../../../../../hooks/useExpand'
import { getLabel } from '../../../../../../../utils/getLabel'
import { DescriptionBtnWithPopup } from '../../../../../../buttons/PromptBtn/DescriptionBtnWithPopup'
import AppButton from '../../../../../../buttons/AppButton/AppButton'
import PromptBtn from '../../../../../../buttons/PromptBtn/PromptBtn'
import { ViewOnlyWrapper } from '../../../../../../ViewOnly/ViewOnlyWrapper'
import {
    addItemToArrayBeginning,
    addItemToArrayEnd,
    deleteRuleBlockArrayCardItem,
    modifyRuleCardConfig,
} from '../../../../../../../store/actions/ruleCardActions'
import { generateElementID } from '../../../../../../../utils/generateElementID'
import { getBlockFooterData } from '../../../../../../../utils/blockFooterData'
import { ruleCardActions } from '../../../../../../../store/slices/ruleCardSlice'
import { getEmptyCard } from '../../../../utils/getEmptyCard'
import { RuleCardsList } from './RuleCardsList'

interface Props {
    dataPath: string
    pathWithElementID: string | undefined
    config?: any
    name: string
    schema: any
    wrapper?: string
    levelDeep?: number
    isViewOnly?: boolean
    element_id?: string
    inCard?: boolean
    isRuleActive?: boolean
    isParentDisabled?: boolean
}

export const RuleCardsContainer = ({
    dataPath,
    pathWithElementID,
    config,
    name,
    schema = {},
    isViewOnly,
    element_id,
    inCard,
    isRuleActive,
    isParentDisabled,
}: Props) => {
    const {
        is_required: isRequired,
        is_empty_as_null: isEmptyAsNull,
        is_auxiliary: isCollapsed,
    } = schema

    const dispatch = useAppDispatch()
    const [cardToDeleteElementID, setCardToDeleteElementID] = useState<
        null | string
    >(null)

    const focusTimeoutRef = useRef<null | NodeJS.Timeout>(null)
    const [focusIndex, setFocusIndex] = useState(-1)
    const [isNewCardAdded, setIsNewCardAdded] = useState(false)

    const path = dataPath.split(PATH_JOINER)
    const localId = element_id ? `${element_id}-${name}` : `${path}-${name}`
    const ariaID = generateAriaId()
    const { expandBtn, expanded, expand, toggle } = useExpand({
        isExpanded: !isCollapsed,
        uniqueID: localId,
        ariaID,
        className: `${style.button} alwaysActive`,
    })

    const componentSchema = schema.children?.children || schema.children
    const schemaKeys = Object.keys(componentSchema)

    const label = getLabel(name, schema.name)
    const styleHeader = [
        style.header,
        inCard && !isRuleActive ? 'rule-inactive-block' : 'app-bg-navi-blue',
        'rounded border p-0',
    ]
    const errorKey = pathWithElementID
        ? pathWithElementID
        : path.join(PATH_JOINER)

    const addNewCardToTheBeginning = (): void => {
        if (focusTimeoutRef.current) {
            clearTimeout(focusTimeoutRef.current)
        }
        dispatch(
            addItemToArrayBeginning({
                path,
                data: getEmptyCard(schemaKeys, componentSchema),
            })
        )
        expand()
        setFocusIndex(0)
        setIsNewCardAdded(true)
        focusTimeoutRef.current = setTimeout(
            () => setIsNewCardAdded(false),
            2500
        )
    }

    const addNewCardToTheEnd = (): void => {
        if (focusTimeoutRef.current) {
            clearTimeout(focusTimeoutRef.current)
        }
        dispatch(
            addItemToArrayEnd({
                path,
                data: getEmptyCard(schemaKeys, componentSchema),
            })
        )
        expand()
        setFocusIndex(config.length)
        setIsNewCardAdded(true)
        focusTimeoutRef.current = setTimeout(
            () => setIsNewCardAdded(false),
            2500
        )
    }

    const footerData = getBlockFooterData(
        config,
        0,
        addNewCardToTheEnd,
        'tooltips.addToEnd',
        []
    )

    useEffect(() => {
        // generate element_id
        const noElemIdItemIndexes = config.reduce(
            (acc: any[], item: any, idx: number) => {
                if (item && !item.element_id) {
                    acc.push(idx)
                }
                return acc
            },
            []
        )
        if (noElemIdItemIndexes.length) {
            const configClone = _cloneDeep(config)
            noElemIdItemIndexes.reduce(
                (acc: any, noElemIdItemIndex: number) => {
                    acc[noElemIdItemIndex].element_id = generateElementID()
                    return acc
                },
                configClone
            )
            dispatch(modifyRuleCardConfig({ path, data: configClone }))
        }
    }, [dispatch])

    useEffect(() => {
        if (cardToDeleteElementID !== null) {
            const filteredArrayItems = config.filter(
                (cardItem: any) => cardItem.element_id !== cardToDeleteElementID
            )
            const errorPathWithElementID = pathWithElementID
                ? [
                      ...pathWithElementID.split(PATH_JOINER),
                      cardToDeleteElementID,
                  ]
                : [...dataPath.split(PATH_JOINER), cardToDeleteElementID]

            const itemToDeleteErrorKey =
                errorPathWithElementID.join(PATH_JOINER)

            dispatch(
                deleteRuleBlockArrayCardItem({
                    path,
                    data: filteredArrayItems,
                    errorKeyName: itemToDeleteErrorKey,
                })
            )
            setCardToDeleteElementID(null)
        }
    }, [cardToDeleteElementID]) // eslint-disable-line

    useEffect(() => {
        if (isRequired && isEmptyAsNull && !config?.length) {
            dispatch(
                ruleCardActions.setRuleConfigInvalid({ errorKeyName: errorKey })
            )
        }
        if (isRequired && config?.length) {
            dispatch(
                ruleCardActions.setRuleConfigValid({ errorKeyName: errorKey })
            )
        }
    }, [config]) // eslint-disable-line

    useLayoutEffect(() => {
        if (!config.length && isRequired && isEmptyAsNull) {
            addNewCardToTheEnd()
        }
    }, []) // eslint-disable-line

    return (
        <ViewOnlyWrapper
            disableEvents={!!isViewOnly}
            className={`w-100 border-left border-right border-bottom d-flex flex-column rounded mt-2 mb-2 position-relative ${
                style.cardsContainer
            }`}
        >
            <div className={styleHeader.join(' ')}>
                <div
                    className="w-100"
                    onClick={toggle}
                >
                    <h4
                        className={`${style.title} ${!schema.namme && style.capitalized}`}
                    >
                        {label}
                    </h4>
                </div>
                {isRequired &&
                    isEmptyAsNull &&
                    !Object.values(config).length &&
                    !isParentDisabled && (
                        <PromptBtn
                            className="me-2"
                            type="validation"
                            prompt="At least 1 item required"
                        />
                    )}
                <AppButton
                    variant={'add'}
                    onClick={addNewCardToTheBeginning}
                    className="btn-success btn btn-add p-1 mt-1 me-3 mb-1 ms-auto ps-3 pe-3"
                    tooltip={
                        inCard
                            ? 'tooltips.addNewItem'
                            : 'tooltips.addToBeginning'
                    }
                />
                {schema.description && (
                    <DescriptionBtnWithPopup
                        type={'small'}
                        header={label}
                        description={schema.description}
                        className="ms-auto me-2 text-white"
                    />
                )}
                {expandBtn}
            </div>
            <Collapse in={expanded}>
                <div
                    className={`pt-2 app-bg-primary flex-`}
                    id={ariaID}
                >
                    {config?.length === 0 ? (
                        <div
                            style={{ height: '100px' }}
                            className="w-100 p-relative d-flex align-items-center justify-content-center text-secondary"
                        >
                            <span
                                style={{ opacity: '0.5' }}
                                className="h3 text-secondary"
                            >
                                <FormattedMessage id="table.nodata" />
                            </span>
                        </div>
                    ) : (
                        <>
                            <div className="w-100 p-relative">
                                <div className={style.cardsColum}>
                                    <RuleCardsList
                                        schema={schema}
                                        config={config}
                                        path={path}
                                        inCard={inCard}
                                        setCardToDeleteElementID={
                                            setCardToDeleteElementID
                                        }
                                        isRuleActive={isRuleActive}
                                        focusIndex={focusIndex}
                                        isNewCardAdded={isNewCardAdded}
                                        pathWithElementID={pathWithElementID}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    {footerData?.length > 0 && (
                        <div
                            style={{ background: '#C5DBEA' }}
                            className="d-flex p-1 justify-content-end"
                        >
                            {footerData}
                            {expandBtn}
                        </div>
                    )}
                </div>
            </Collapse>
        </ViewOnlyWrapper>
    )
}
